/**
 * Утилиты для работы с компонентом MemoryGame
 */

/**
 * Проверяет, что данные для игры "Память" имеют правильный формат
 * @param {Array|string} data - Данные для игры (массив объектов или JSON строка)
 * @param {string} context - Контекст проверки для логгирования
 * @returns {Array} - Правильно форматированные данные или пустой массив
 */
export const validateMemoryGameData = (data, context = 'MemoryGame') => {
  try {
    // Если данные не заданы, возвращаем пустой массив
    if (!data) {
      console.warn(`${context}: Данные не заданы`);
      return [];
    }
    
    // Если данные - строка, пытаемся распарсить
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch (error) {
        console.error(`${context}: Ошибка при парсинге JSON строки`, error);
        return [];
      }
    }
    
    // Проверяем, что данные - массив
    if (!Array.isArray(parsedData)) {
      console.error(`${context}: Данные не являются массивом`, parsedData);
      return [];
    }
    
    // Проверяем, что каждый элемент имеет text и pairId
    const validData = parsedData.filter(item => {
      if (!item || typeof item !== 'object') {
        console.warn(`${context}: Элемент не является объектом`, item);
        return false;
      }
      
      if (!('text' in item) || !('pairId' in item)) {
        console.warn(`${context}: Элемент не содержит text или pairId`, item);
        return false;
      }
      
      return true;
    });
    
    if (validData.length !== parsedData.length) {
      console.warn(`${context}: Некоторые элементы были отфильтрованы из-за неправильного формата`);
    }
    
    return validData;
  } catch (error) {
    console.error(`${context}: Непредвиденная ошибка при проверке данных`, error);
    return [];
  }
};

/**
 * Проверяет, являются ли данные для игры "Память" корректными (есть пары карточек)
 * @param {Array} data - Данные для игры (массив объектов)
 * @returns {boolean} - true если данные корректны, false в противном случае
 */
export const isValidMemoryGameData = (data) => {
  try {
    if (!Array.isArray(data) || data.length === 0) {
      return false;
    }
    
    // Проверяем, что все карточки имеют пары
    const pairIds = data.map(item => item.pairId);
    const pairCounts = {};
    
    pairIds.forEach(id => {
      pairCounts[id] = (pairCounts[id] || 0) + 1;
    });
    
    // Все pairId должны встречаться четное количество раз (обычно 2)
    return Object.values(pairCounts).every(count => count >= 2 && count % 2 === 0);
  } catch (error) {
    console.error('Ошибка при проверке корректности данных Memory Game', error);
    return false;
  }
};

/**
 * Создает пример данных для игры "Память"
 * @param {number} pairsCount - Количество пар карточек
 * @returns {Array} - Массив объектов для игры
 */
export const createSampleMemoryGameData = (pairsCount = 3) => {
  const sampleData = [];
  
  for (let i = 1; i <= pairsCount; i++) {
    // Добавляем первую карточку пары
    sampleData.push({
      text: `Карточка ${i}`,
      pairId: i
    });
    
    // Добавляем вторую карточку пары
    sampleData.push({
      text: `Карточка ${i}`,
      pairId: i
    });
  }
  
  return sampleData;
};

const memoryGameUtils = {
  validateMemoryGameData,
  isValidMemoryGameData,
  createSampleMemoryGameData
};

export default memoryGameUtils; 